import { FormatQty } from '../production/productionModel';

/**
 * NÚCLEO NÃO-VISUAL DO REPACK (M7) — leitura dos envelopes do backend e a
 * montagem do corpo das chamadas.
 *
 * ⚠️ O QUE NÃO MORA AQUI, DE PROPÓSITO: **conta de saldo**. Nenhuma função
 * deste arquivo soma, subtrai ou projeta quantidade. O invariante do §3 tem UM
 * juiz — o motor do servidor (`repackEngine.js`) —, e a prévia (`dry_run`)
 * devolve o razão pronto (`balance.holders[].qty_before/qty_after` e
 * `totals_by_product`). Repetir a aritmética no navegador criaria uma segunda
 * verdade que só diverge quando ninguém está olhando: a tela prometeria um
 * saldo e o banco gravaria outro. Aqui só se LÊ o que o servidor decidiu.
 *
 * `FormatQty` vem do M6 (mesma coluna `DECIMAL(18,6)`, mesma apresentação) —
 * reuso, não duplicação (CLAUDE.md §4).
 */

export { FormatQty };

/** As três operações do módulo. PICK é o picking fracionado (NÃO gera LPN). */
export const REPACK_SPLIT = 'SPLIT';
export const REPACK_MERGE = 'MERGE';
export const REPACK_PICK = 'PICK';
export const REPACK_OPERATIONS = Object.freeze([REPACK_SPLIT, REPACK_MERGE, REPACK_PICK]);

/** Os três lugares onde o saldo pode viver (§3), na ordem em que a UI os mostra. */
export const HOLDER_LPN = 'LPN';
export const HOLDER_PRODUCTION_ORDER = 'PRODUCTION_ORDER';
export const HOLDER_SHIPMENT_VOLUME = 'SHIPMENT_VOLUME';

/** Destinos possíveis do fracionado. */
export const PICK_DESTINATION_PRODUCTION_ORDER = 'PRODUCTION_ORDER';
export const PICK_DESTINATION_SHIPMENT = 'SHIPMENT';

/** Códigos de erro de negócio do M7 que a UI trata como INSTRUÇÃO, não falha. */
export const ERROR_PICK_WITHOUT_DESTINATION = 'WMS26_REPACK_PICK_WITHOUT_DESTINATION';
export const ERROR_LPN_CREATION_FORBIDDEN = 'WMS26_REPACK_LPN_CREATION_FORBIDDEN';
export const ERROR_LPN_CLOSED = 'WMS26_REPACK_LPN_CLOSED';
export const ERROR_INSUFFICIENT_QTY = 'WMS26_REPACK_INSUFFICIENT_QTY';
export const ERROR_SUPPLIER_LOT_AMBIGUOUS = 'WMS26_REPACK_SUPPLIER_LOT_AMBIGUOUS';
export const ERROR_BALANCE_BROKEN = 'WMS26_REPACK_BALANCE_BROKEN';

/** Código do aviso de FIFO (§6.2 — aviso, jamais trava). */
export const FIFO_WARNING_CODE = 'FIFO_OLDER_LPN';

/** Romaneios que ainda RECEBEM mercadoria (os demais o backend recusa). */
export const OPEN_OUTBOUND_STATUSES = Object.freeze(['pending', 'idle', 'picking', 'ready_to_collect']);

/** O código do erro, venha ele solto ou dentro do envelope de resposta. */
export function ErrorCodeOf(error) {
    return error?.code ?? error?.details?.code ?? error?.response?.data?.error?.code ?? null;
}

export function IsPickWithoutDestinationError(error) {
    return ErrorCodeOf(error) === ERROR_PICK_WITHOUT_DESTINATION;
}

/**
 * 403 de criação de LPN: NÃO é erro de operação, é a regra do §6.2 ("só o
 * Supervisor cria LPN") chegando à tela. Quem lê isso tem que sair sabendo o
 * que fazer — pedir a etiqueta —, não achar que o sistema quebrou.
 */
export function IsLpnCreationForbiddenError(error) {
    const code = ErrorCodeOf(error);
    if (code === ERROR_LPN_CREATION_FORBIDDEN) {
        return true;
    }
    // 403 SEM código só pode ser esta recusa nas rotas do M7 (as duas
    // permissões de entrada já foram checadas pela rota). Com código, quem
    // manda é o código — 403 genérico não vira "peça a etiqueta".
    return !code && error?.status === 403;
}

/** Texto vazio, `null` e `undefined` são o mesmo "sem lote". */
export function NormalizeSupplierLot(value) {
    const text = String(value ?? '').trim();
    return text === '' ? null : text;
}

/** Aceita vírgula decimal (é o que o teclado do galpão produz). */
export function ParseQtyInput(raw) {
    const text = String(raw ?? '')
        .replace(',', '.')
        .trim();
    if (text === '') {
        return null;
    }
    const number = Number(text);
    return Number.isFinite(number) ? number : null;
}

/**
 * Uma linha de edição por ITEM do pallet — o item é (produto + lote), que é a
 * identidade do saldo no §3. `available` é o número que o BACKEND devolveu no
 * item; a tela o exibe, nunca o recalcula.
 */
export function BuildDraftLines(items = []) {
    return items.map((item) => ({
        key: item.id ?? `${item.product_id}|${item.supplier_lot ?? ''}`,
        product_id: item.product_id,
        sku: item.sku ?? null,
        name: item.name ?? null,
        supplier_lot: item.supplier_lot ?? null,
        expiry_date: item.expiry_date ?? null,
        available: item.qty ?? 0,
        qtyText: '',
    }));
}

/**
 * As linhas do wire (`{product_id, supplier_lot?, qty}`) a partir do rascunho.
 * Só entra o que tem quantidade > 0 — linha em branco é "não mexi nesta".
 *
 * ⚠️ O lote SEMPRE viaja quando o item tem um. O backend recusa (409
 * `SUPPLIER_LOT_AMBIGUOUS`) quando o produto está em mais de um lote e o wire
 * não diz qual — e ele está certo em recusar: escolher sozinho apagaria a
 * rastreabilidade que o recall do §6.7 usa.
 */
export function BuildWireLines(draftLines = []) {
    const lines = [];
    for (const line of draftLines) {
        const qty = ParseQtyInput(line.qtyText);
        if (qty === null || !(qty > 0)) {
            continue;
        }
        const wire = { product_id: line.product_id, qty };
        const lot = NormalizeSupplierLot(line.supplier_lot);
        if (lot !== null) {
            wire.supplier_lot = lot;
        }
        lines.push(wire);
    }
    return lines;
}

/** Os produtos tocados por um rascunho — é o que o seletor FIFO consulta. */
export function DraftProductIds(draftLines = []) {
    const ids = new Set();
    for (const line of draftLines) {
        const qty = ParseQtyInput(line.qtyText);
        if (qty !== null && qty > 0 && line.product_id) {
            ids.add(line.product_id);
        }
    }
    return [...ids];
}

/**
 * O RAZÃO da prévia, normalizado para a tela. Nada é somado aqui: `qty_before`,
 * `qty_after`, `emptied` e `balanced` vêm prontos do motor.
 */
export function ParseRepackBalance(payload) {
    const balance = payload?.balance ?? null;
    if (!balance || typeof balance !== 'object') {
        return null;
    }
    const holders = Array.isArray(balance.holders) ? balance.holders : [];
    const totals = Array.isArray(balance.totals_by_product) ? balance.totals_by_product : [];
    return {
        holders: holders.map((holder) => ({
            kind: holder.kind,
            key: holder.key,
            label: holder.label ?? holder.key,
            qty_before: holder.qty_before,
            qty_after: holder.qty_after,
            emptied: Boolean(holder.emptied),
            balances: Array.isArray(holder.balances) ? holder.balances : [],
        })),
        totals,
        balanced: balance.balanced !== false,
    };
}

/** Avisos da prévia. FIFO é o único hoje — e é AVISO (§6.2), nunca bloqueio. */
export function ParseRepackWarnings(payload) {
    const warnings = payload?.warnings;
    return Array.isArray(warnings) ? warnings : [];
}

/** O que a prévia diz que vai NASCER e o que vai FECHAR. */
export function ParseRepackEffects(payload) {
    return {
        wouldCreate: Array.isArray(payload?.would_create_lpns) ? payload.would_create_lpns : [],
        wouldClose: Array.isArray(payload?.would_close_lpns) ? payload.would_close_lpns : [],
    };
}

/**
 * Sinal do movimento de um holder, lido do que o servidor devolveu. É
 * comparação de dois números que JÁ vieram prontos — não é o cálculo do saldo.
 */
export function HolderDirection(holder) {
    const before = Number(holder?.qty_before ?? 0);
    const after = Number(holder?.qty_after ?? 0);
    if (after > before) {
        return 'IN';
    }
    if (after < before) {
        return 'OUT';
    }
    return 'SAME';
}

// ── Rascunhos da tela (estado do console) ───────────────────────────────────

let draftKeySeed = 0;

function nextDraftKey(prefix) {
    draftKeySeed += 1;
    return `${prefix}-${draftKeySeed}`;
}

/** Uma ORIGEM: o código digitado/bipado, o pallet carregado e as linhas. */
export function CreateSourceDraft() {
    return { key: nextDraftKey('src'), code: '', lpn: null, lines: [], loading: false, loadError: null };
}

/** Modos de destino. NEW só é oferecido a quem pode criar LPN (§6.2). */
export const DESTINATION_EXISTING = 'EXISTING';
export const DESTINATION_NEW = 'NEW';

/** Um DESTINO: pallet existente (código) ou pallet novo (prefixo/posição/depósito). */
export function CreateDestinationDraft() {
    return {
        key: nextDraftKey('dst'),
        mode: DESTINATION_EXISTING,
        code: '',
        prefixCode: '',
        locationId: '',
        depositId: '',
        // `null` = herda o volumoso do pallet de origem (é o default do backend).
        isBulky: null,
        lines: [],
    };
}

/** Um rótulo estável para a linha de saldo (produto + lote). */
export function BalanceLineTitle(row) {
    const sku = row?.sku || row?.product_id || '—';
    const lot = NormalizeSupplierLot(row?.supplier_lot);
    return lot === null ? String(sku) : `${sku} · ${lot}`;
}

/** Chave de lista estável para uma linha de saldo. */
export function BalanceLineKey(row) {
    return `${row?.product_id ?? ''}|${row?.supplier_lot ?? ''}`;
}
